<script setup>
import { onBeforeUnmount, ref } from "vue";
import { appVersion } from "@/globalVars.js";

const EASTER_EGG_CLICK_TARGET = 10;
const EASTER_EGG_RESET_MS = 1000;
const EASTER_EGG_IMAGE_SOURCES = [
  "/img/easter-eggs/13714.png"
];

const versionClickCount = ref(0);
const showVersionEasterEgg = ref(false);
const imageLoadFailed = ref(false);
const imageSourceIndex = ref(0);

let resetClickCountTimer = null;

function clearClickCountTimer() {
  if (!resetClickCountTimer) return;
  window.clearTimeout(resetClickCountTimer);
  resetClickCountTimer = null;
}

function scheduleClickCountReset() {
  clearClickCountTimer();
  resetClickCountTimer = window.setTimeout(() => {
    versionClickCount.value = 0;
    resetClickCountTimer = null;
  }, EASTER_EGG_RESET_MS);
}

function openVersionEasterEgg() {
  clearClickCountTimer();
  versionClickCount.value = 0;
  imageLoadFailed.value = false;
  imageSourceIndex.value = 0;
  showVersionEasterEgg.value = true;
  window.addEventListener("keydown", handleEasterEggKeydown);
}

function closeVersionEasterEgg() {
  showVersionEasterEgg.value = false;
  window.removeEventListener("keydown", handleEasterEggKeydown);
}

function handleVersionClick() {
  versionClickCount.value += 1;

  if (versionClickCount.value >= EASTER_EGG_CLICK_TARGET) {
    openVersionEasterEgg();
    return;
  }

  scheduleClickCountReset();
}

function handleEasterEggKeydown(event) {
  if (event.key === "Escape") {
    closeVersionEasterEgg();
  }
}

function handleEasterEggImageError() {
  const nextIndex = imageSourceIndex.value + 1;
  if (nextIndex < EASTER_EGG_IMAGE_SOURCES.length) {
    imageSourceIndex.value = nextIndex;
    return;
  }

  imageLoadFailed.value = true;
}

onBeforeUnmount(() => {
  clearClickCountTimer();
  window.removeEventListener("keydown", handleEasterEggKeydown);
});
</script>

<template>
  <button
    class="version-button"
    type="button"
    aria-label="Version de la aplicacion"
    @click="handleVersionClick"
  >
    {{ appVersion }}
  </button>

  <teleport to="body">
    <transition name="version-easter-egg">
      <div
        v-if="showVersionEasterEgg"
        class="version-easter-egg"
        role="dialog"
        aria-modal="true"
        aria-label="Sorpresa"
        @click="closeVersionEasterEgg"
      >
        <div class="version-easter-egg__stage">
          <img
            v-if="!imageLoadFailed"
            class="version-easter-egg__image"
            :src="EASTER_EGG_IMAGE_SOURCES[imageSourceIndex]"
            alt="Sorpresa"
            @error="handleEasterEggImageError"
          />
          <div v-else class="version-easter-egg__fallback">
            <span class="version-easter-egg__fallback-title">boo</span>
            <span class="version-easter-egg__fallback-text">
              Falta version-surprise en /img/easter-eggs
            </span>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<style scoped>
.version-button {
  background: transparent;
  border: 0;
  color: inherit;
  cursor: default !important;
  font: inherit;
  line-height: inherit;
  padding: 0;
  user-select: none;
}

.version-button:focus-visible {
  outline: none;
}

.version-easter-egg {
  align-items: center;
  background: rgba(0, 0, 0, 0.72);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 1.5rem;
  position: fixed;
  z-index: 1090;
  animation: surreal-bg 1.8s linear infinite;
}

.version-easter-egg__stage {
  animation: version-egg-pop 500ms cubic-bezier(0.18, 1.45, 0.35, 1) both,
    surreal-drift 3.8s ease-in-out 380ms infinite,
    surreal-glow 1.2s linear 380ms infinite;
  max-width: min(86vw, 820px);
  transform-origin: center;
  width: 100%;
}

.version-easter-egg__image {
  border-radius: 8px;
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.45);
  display: block;
  max-height: 76vh;
  object-fit: contain;
  width: 100%;
  animation: surreal-chroma 1.4s linear infinite;
}

.version-easter-egg__fallback {
  align-items: center;
  background: #111827;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  color: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-height: 16rem;
  justify-content: center;
  padding: 2rem;
  text-align: center;
}

.version-easter-egg__fallback-title {
  font-size: 2.4rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.version-easter-egg__fallback-text {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.82rem;
}

.version-easter-egg-enter-active,
.version-easter-egg-leave-active {
  transition: opacity 180ms ease;
}

.version-easter-egg-enter-from,
.version-easter-egg-leave-to {
  opacity: 0;
}

@keyframes version-egg-pop {
  0% {
    opacity: 0;
    transform: scale(0.08) rotate(-14deg);
  }

  45% {
    opacity: 1;
    transform: scale(1.18) rotate(6deg);
  }

  72% {
    opacity: 1;
    transform: scale(0.94) rotate(-3deg);
  }

  100% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }
}

@keyframes surreal-bg {
  0%   { background: rgba(160, 0,   0,   0.82); }
  16%  { background: rgba(140, 70,  0,   0.82); }
  33%  { background: rgba(100, 120, 0,   0.82); }
  50%  { background: rgba(0,   130, 40,  0.82); }
  66%  { background: rgba(0,   30,  180, 0.82); }
  83%  { background: rgba(100, 0,   180, 0.82); }
  100% { background: rgba(160, 0,   0,   0.82); }
}

@keyframes surreal-drift {
  0%, 100% {
    transform: translate3d(0, 0, 0) rotate(0deg) skew(0deg);
  }

  18% {
    transform: translate3d(-16px, 9px, 0) rotate(-4deg) skewX(-2deg);
  }

  36% {
    transform: translate3d(13px, -11px, 0) rotate(4.5deg) skewX(2.5deg) scale(1.05);
  }

  55% {
    transform: translate3d(-9px, 13px, 0) rotate(-2.5deg) skewY(2deg) scale(0.96);
  }

  74% {
    transform: translate3d(17px, -6px, 0) rotate(3.5deg) skewX(-1.5deg) scale(1.03);
  }

  88% {
    transform: translate3d(-5px, 4px, 0) rotate(-1.5deg) scale(1.01);
  }
}

@keyframes surreal-chroma {
  0%   { filter: drop-shadow(10px  2px 0 rgba(255, 0,   0,   0.85)) drop-shadow(-10px -2px 0 rgba(0,   255, 255, 0.85)) saturate(2.5) hue-rotate(0deg);   }
  16%  { filter: drop-shadow(10px -2px 0 rgba(255, 140, 0,   0.85)) drop-shadow(-10px  2px 0 rgba(0,   60,  255, 0.85)) saturate(3.0) hue-rotate(60deg);  }
  33%  { filter: drop-shadow(8px   4px 0 rgba(220, 255, 0,   0.85)) drop-shadow(-8px  -4px 0 rgba(80,  0,   255, 0.85)) saturate(3.2) hue-rotate(120deg); }
  50%  { filter: drop-shadow(12px  0px 0 rgba(0,   255, 80,  0.85)) drop-shadow(-12px  0px 0 rgba(255, 0,   180, 0.85)) saturate(3.5) hue-rotate(180deg); }
  66%  { filter: drop-shadow(8px  -4px 0 rgba(0,   200, 255, 0.85)) drop-shadow(-8px   4px 0 rgba(255, 60,  0,   0.85)) saturate(3.0) hue-rotate(240deg); }
  83%  { filter: drop-shadow(10px  2px 0 rgba(200, 0,   255, 0.85)) drop-shadow(-10px -2px 0 rgba(60,  255, 0,   0.85)) saturate(2.8) hue-rotate(300deg); }
  100% { filter: drop-shadow(10px  2px 0 rgba(255, 0,   0,   0.85)) drop-shadow(-10px -2px 0 rgba(0,   255, 255, 0.85)) saturate(2.5) hue-rotate(360deg); }
}

@keyframes surreal-glow {
  0%   { filter: drop-shadow(0 0 25px rgba(255, 0,   0,   1)) drop-shadow(0 0 60px rgba(255, 0,   0,   0.5));   }
  16%  { filter: drop-shadow(0 0 25px rgba(255, 140, 0,   1)) drop-shadow(0 0 60px rgba(255, 140, 0,   0.5));   }
  33%  { filter: drop-shadow(0 0 25px rgba(220, 255, 0,   1)) drop-shadow(0 0 60px rgba(220, 255, 0,   0.5));   }
  50%  { filter: drop-shadow(0 0 25px rgba(0,   255, 80,  1)) drop-shadow(0 0 60px rgba(0,   255, 80,  0.5));   }
  66%  { filter: drop-shadow(0 0 25px rgba(0,   140, 255, 1)) drop-shadow(0 0 60px rgba(0,   140, 255, 0.5));   }
  83%  { filter: drop-shadow(0 0 25px rgba(200, 0,   255, 1)) drop-shadow(0 0 60px rgba(200, 0,   255, 0.5));   }
  100% { filter: drop-shadow(0 0 25px rgba(255, 0,   0,   1)) drop-shadow(0 0 60px rgba(255, 0,   0,   0.5));   }
}

@media (prefers-reduced-motion: reduce) {
  .version-easter-egg,
  .version-easter-egg__stage,
  .version-easter-egg__image {
    animation: none;
    filter: none;
  }
}
</style>
